/**
 * Profil.
 *
 * Profilbild und E-Mail-Adresse lassen sich hier ändern. Ein Passwortbereich
 * fehlt bewusst: die Anmeldung läuft über das Microsoft-Konto, dort wird auch
 * das Passwort verwaltet.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Modal } from '../komponenten/Modal';
import type { Nutzer, Profil } from '../typen';
import './profil.css';

export function ProfilModal() {
  const { modalSchliessen, nutzer, toastZeigen } = useKora();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<'bild' | 'mail' | 'anmeldung'>('bild');
  const [mail, setMail] = useState('');
  const [meldung, setMeldung] = useState<{ text: string; gut: boolean } | null>(null);

  const abfrage = useQuery({
    queryKey: ['profil'],
    queryFn: () => api.hole<Profil>('/profil'),
  });

  useEffect(() => {
    if (abfrage.data && !mail) setMail(abfrage.data.email);
  }, [abfrage.data, mail]);

  function neuLaden() {
    void queryClient.invalidateQueries({ queryKey: ['ich'] });
    void queryClient.invalidateQueries({ queryKey: ['profil'] });
    void queryClient.invalidateQueries({ queryKey: ['meta'] });
  }

  const bildSetzen = useMutation({
    mutationFn: (bild: string) => api.ersetze<Nutzer>('/profil/bild', { bild }),
    onSuccess: () => {
      neuLaden();
      toastZeigen('Profilbild gespeichert.');
    },
    onError: (f) =>
      setMeldung({ text: f instanceof Error ? f.message : 'Speichern fehlgeschlagen.', gut: false }),
  });

  const bildWeg = useMutation({
    mutationFn: () => api.loesche<Nutzer>('/profil/bild'),
    onSuccess: neuLaden,
  });

  const mailSetzen = useMutation({
    mutationFn: () => api.aendere<{ meldung: string }>('/profil/email', { email: mail.trim() }),
    onSuccess: (a) => {
      neuLaden();
      setMeldung({ text: a.meldung, gut: true });
    },
    onError: (f) =>
      setMeldung({ text: f instanceof Error ? f.message : 'Speichern fehlgeschlagen.', gut: false }),
  });

  const p = abfrage.data;

  return (
    <Modal
      titel="Profil"
      untertitel={nutzer ? `${nutzer.name} · ${nutzer.rolle}` : ''}
      breite={560}
      zeigeSchliessen
      onSchliessen={modalSchliessen}
      fuss={
        <button type="button" className="pr-fertig" onClick={modalSchliessen}>
          Fertig
        </button>
      }
    >
      <div className="pr-tabs">
        {(
          [
            { k: 'bild', l: 'Profilbild' },
            { k: 'mail', l: 'E-Mail' },
            { k: 'anmeldung', l: 'Anmeldung' },
          ] as const
        ).map((t) => (
          <button
            key={t.k}
            type="button"
            className={`pr-tab ${tab === t.k ? 'aktiv' : ''}`}
            onClick={() => {
              setTab(t.k);
              setMeldung(null);
            }}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'bild' && (
        <div className="pr-bildbereich">
          <span
            className="eh-avatar pr-bild"
            style={nutzer?.profilbild ? { backgroundImage: `url(${nutzer.profilbild})` } : undefined}
          >
            {nutzer?.profilbild ? '' : nutzer?.initialen}
          </span>
          <div className="pr-bildaktionen">
            <label className="pr-knopf-sek">
              Bild auswählen
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const datei = e.target.files?.[0];
                  if (!datei) return;
                  const leser = new FileReader();
                  leser.onload = () => bildSetzen.mutate(String(leser.result));
                  leser.readAsDataURL(datei);
                  e.target.value = '';
                }}
              />
            </label>
            <button type="button" className="pr-knopf-sek" onClick={() => bildWeg.mutate()}>
              Bild entfernen
            </button>
            <div className="modal-hinweis">
              PNG, JPG, WEBP oder GIF bis 2 MB. Das Bild erscheint sofort in der Seitenleiste.
              Künftig sollen die Bilder aus Monday kommen.
            </div>
          </div>
        </div>
      )}

      {tab === 'mail' && (
        <>
          <label className="modal-feld">
            <span>E-Mail-Adresse</span>
            <input
              className="modal-eingabe"
              value={mail}
              onChange={(e) => {
                setMail(e.target.value);
                setMeldung(null);
              }}
            />
          </label>
          <div className="modal-hinweis">
            Aus diesem Postfach gehen die automatischen Erinnerungen raus, wenn du Versender bist.
            Sie muss dem Microsoft-Konto entsprechen.
          </div>
          <div>
            <button
              type="button"
              className="pr-knopf-prim"
              disabled={mailSetzen.isPending}
              onClick={() => mailSetzen.mutate()}
            >
              {mailSetzen.isPending ? 'Wird gespeichert …' : 'E-Mail speichern'}
            </button>
          </div>
        </>
      )}

      {tab === 'anmeldung' && p && (
        <>
          <div className={`eh-banner ${p.passwortVerwaltung.extern ? 'eh-banner--blau' : 'eh-banner--gelb'}`}>
            {p.passwortVerwaltung.hinweis}
          </div>
          {p.passwortVerwaltung.url && (
            <div>
              <a
                href={p.passwortVerwaltung.url}
                target="_blank"
                rel="noreferrer"
                className="pr-knopf-prim pr-link"
              >
                Microsoft-Kontosicherheit öffnen ↗
              </a>
            </div>
          )}
          <div className="modal-hinweis">
            Wer welchen Bereich sieht, legt die Geschäftsführung im Admin Center fest.
          </div>
        </>
      )}

      {meldung && (
        <div
          className="pr-meldung"
          style={{ color: meldung.gut ? 'var(--eh-gruen-ampel)' : 'var(--eh-rot)' }}
        >
          {meldung.text}
        </div>
      )}
    </Modal>
  );
}
